import type { FulfillmentOrderLine } from "../types";
import type { EntryExistingDraft, EntryParseBaseInput, EntryParseInput, EntryParseResult } from "./confirmationEntryTypes";
import { parseCrewEntry } from "./confirmationCrewEntryService";
import { createExistingEntrySourceMatcher, diffExistingEntry, singleExistingEntryPatch, type ExistingConfirmationEntry, type ExistingEntryParseResult } from "./confirmationExistingEntryShared";
import { confirmationTravelerRoleLabel, validateFulfillmentLineDraft } from "./fulfillmentOrderEditorService";
import { confirmationDecimalInput } from "./confirmationDecimalDisplay";
import { confirmationBillingCountUnit } from "./confirmationEditorView";

/** 导游原行独立生成编辑文本，保留原实体和未表达字段。 */
export function prepareExistingCrewEntry(base: FulfillmentOrderLine, routeDays: EntryParseInput["routeDays"]): ExistingConfirmationEntry {
  const range = base.dayExpression || base.dayNumbers?.join(",") || routeDays.filter(day => base.serviceDates?.includes(day.date || "") || day.date === base.serviceDateFrom).map(day => day.dayNo).join(",");
  const quantity = confirmationDecimalInput(base.quantity), count = confirmationDecimalInput(base.billingCount), price = confirmationDecimalInput(base.unitPriceAmount);
  const role = confirmationTravelerRoleLabel(base.travelerRole);
  const input: EntryParseInput = { category: "crew", text: "", range, routeDays, candidates: [], allowZeroQuantity: !base.id.startsWith("local-"), settlement: base.settlementSupplierId && base.settlementSupplierName ? { id: base.settlementSupplierId, name: base.settlementSupplierName } : null };
  if (base.fulfillmentSupplierId && base.fulfillmentSupplierName) input.provider = { id: base.fulfillmentSupplierId, name: base.fulfillmentSupplierName, snapshotResourceType: "导游" };
  const period = confirmationBillingCountUnit(base);
  const knownPeriod = ["天", "晚", "次"].includes(period) ? period : "";
  const formula = `${role}${quantity}${base.unit === "人" ? "人" : ""}×${count}${knownPeriod}×${price}元`;
  input.text = `${base.itemNameSnapshot} ${formula}`;
  const parsed = parseCrewEntry(input);
  return { base: structuredClone(base), input, baseline: parsed.errors.length || parsed.items.length !== 1 ? null : parsed.items[0].patch };
}

/** 用本分类解析和日期修正恢复原行基线，再计算原 ID 差异。 */
export function parseExistingCrewEntry(seed: ExistingConfirmationEntry, draftInput: EntryParseBaseInput): ExistingEntryParseResult {
  const input: EntryParseInput = { ...draftInput, category: "crew", allowZeroQuantity: seed.input.allowZeroQuantity };
  const result = parseCrewEntry(input);
  const single = singleExistingEntryPatch(result, input.text);
  if (single.errors.length) return single;
  const parsed = single.patch;
  let recovered = seed.baseline ? null : parseCrewEntry({ ...input, text: seed.input.text, range: seed.input.range });
  const dateEdited = input.range !== seed.input.range;
  const repairedDates = Boolean(dateEdited && recovered?.errors.length && recovered.errors.every(error => error.field === "range"));
  if (repairedDates) {
    // 基线只借用修正后的日期；主体与资源沿用原输入，不能把本次修改当作旧值。
    recovered = parseCrewEntry({ ...seed.input, routeDays: input.routeDays, range: parsed.dayExpression || input.range, text: seed.input.text });
  }
  const baseline = seed.baseline || (recovered?.errors.length === 0 && recovered.items.length === 1 ? recovered.items[0].patch : null);
  return diffExistingEntry(seed, parsed, baseline, input.text, { repairedDates });
}

/** 批量原文先保留完全相同的行，再按唯一项目与角色匹配，拒绝猜测同名费用身份。 */
export function parseCrewDraft(input: EntryParseBaseInput, originals: FulfillmentOrderLine[]): EntryParseResult & { drafts: EntryExistingDraft[] } {
  const seeds = originals.map(row => prepareExistingCrewEntry(row, input.routeDays));
  const drafts = originals.map(base => ({ base, patch: {} as Partial<FulfillmentOrderLine>, removed: false }));
  const result: EntryParseResult = { items: [], errors: [] };
  const sources = [...input.text.matchAll(/[^\n;；]+/g)].filter(source => source[0].trim());
  const used = new Set<number>();
  const matches = new Map<number, number>();
  const identity = (line: Partial<FulfillmentOrderLine>) => JSON.stringify([line.itemNameSnapshot === "导服费" ? "导游服务费" : line.itemNameSnapshot, confirmationTravelerRoleLabel(line.travelerRole)]);
  const canMatchSource = createExistingEntrySourceMatcher(seeds, input.text, identity);
  for (const [index, source] of sources.entries()) {
    const exact = seeds.findIndex((seed, i) => !used.has(i) && seed.input.text.trim() === source[0].trim()
      && canMatchSource(i, { sourceStart: source.index, sourceEnd: source.index + source[0].length }));
    if (exact >= 0) { matches.set(index, exact); used.add(exact); }
  }
  const pending: Array<{ index: number; item: EntryParseResult["items"][number] }> = [];
  for (const [index, source] of sources.entries()) {
    if (matches.has(index)) continue;
    const parsed = parseCrewEntry({ ...input, range: "", text: source[0], allowZeroQuantity: originals.some(row => !row.id.startsWith("local-")) });
    const location = { sourceStart: source.index, sourceEnd: source.index + source[0].length };
    result.errors.push(...parsed.errors.map(error => ({ ...error, ...location })));
    for (const item of parsed.items) pending.push({ index, item: { ...item, ...location, key: `${index}-0` } });
  }
  for (const { index, item } of pending) {
    const candidates = seeds.map((seed, i) => ({ seed, i })).filter(({ seed, i }) => !used.has(i) && identity(seed.baseline || seed.base) === identity(item.patch));
    const sameItems = pending.filter(other => identity(other.item.patch) === identity(item.patch));
    const duplicateOriginal = candidates.some(({ seed }) => seeds.filter(other => other.input.text === seed.input.text).length > 1);
    if (candidates.length && (candidates.length !== 1 || sameItems.length !== 1 || duplicateOriginal)) {
      result.errors.push({ ...item, field: "formula", message: "同名同角色费用无法唯一对应原行，请先逐条修改并应用，避免覆盖其他费用" });
      continue;
    }
    if (candidates.length) { matches.set(index, candidates[0].i); used.add(candidates[0].i); }
    else {
      const errors = validateFulfillmentLineDraft({ ...item.patch, id: "local-crew-preview" } as FulfillmentOrderLine);
      if (errors.length) result.errors.push({ ...item, field: "formula", message: errors.join("；") });
      else result.items.push(item);
    }
  }
  for (const [sourceIndex, seedIndex] of matches) {
    const source = sources[sourceIndex], seed = seeds[seedIndex];
    if (source[0].trim() === seed.input.text.trim()) continue;
    const parsed = parseExistingCrewEntry(seed, { ...seed.input, routeDays: input.routeDays, roleCounts: input.roleCounts, text: source[0] });
    drafts[seedIndex].patch = parsed.patch;
    result.errors.push(...parsed.errors.map(error => ({ ...error, sourceStart: source.index, sourceEnd: source.index + source[0].length })));
  }
  if (result.items.length && seeds.some((_, i) => !used.has(i))) {
    result.errors.push({ raw: input.text, sourceStart: 0, sourceEnd: input.text.length, field: "formula", message: "存在无法对应的原费用和新增费用，请先逐条修改名称或角色，或先明确移除原行后再新增" });
  }
  // 删除重复原文无法确定被移除的主体或日期，须使用原行的明确移除入口。
  if (seeds.some((seed, i) => !used.has(i) && seeds.some((other, j) => used.has(j) && other.input.text === seed.input.text))) {
    result.errors.push({ raw: input.text, sourceStart: 0, sourceEnd: input.text.length, field: "formula", message: "存在原文相同的多条费用，请回主表单独选中对应原行后编辑，不能仅按文本位置删除" });
  }
  if (!result.errors.length) drafts.forEach((draft, i) => { draft.removed = !used.has(i); });
  return { ...result, drafts };
}
